/**
 * Nudges: when a lead sits untouched, the recipients covering its role get the
 * "Have you spoken to…?" text and answer with a digit. The section quotes the
 * exact message so an admin knows what their managers will read.
 */
import { useId } from 'react';
import {
  NUDGE_LIMITS,
  NUDGE_NOTES_QUESTION,
  NUDGE_QUESTION,
  NUDGE_REPLIES,
  type NudgeErrors,
  type NudgeSettings,
} from './types';

const inputClass =
  'mt-1 block min-h-10 w-full rounded-md border border-gray-300 bg-white px-2.5 text-sm text-gray-900 tabular-nums aria-[invalid=true]:border-danger';

export function NudgeEditor({
  value,
  errors,
  onChange,
}: {
  value: NudgeSettings;
  errors: NudgeErrors | undefined;
  /** `touched` names the hours field an edit changed so its inline error can clear at once. */
  onChange: (next: NudgeSettings, touched?: keyof NudgeErrors) => void;
}) {
  const id = useId();
  const hours = (key: 'after_hours' | 'cooldown_hours', label: string, hint: string) => {
    const err = errors?.[key];
    const limits = NUDGE_LIMITS[key];
    return (
      <div className="text-[13px]">
        <label htmlFor={`${id}-${key}`} className="font-medium text-gray-800">{label}</label>
        <input
          id={`${id}-${key}`}
          type="number"
          inputMode="numeric"
          min={limits.min}
          max={limits.max}
          step={1}
          value={Number.isFinite(value[key]) ? value[key] : ''}
          onChange={(e) => onChange({ ...value, [key]: e.target.value === '' ? NaN : Number(e.target.value) }, key)}
          aria-invalid={err ? true : undefined}
          aria-describedby={err ? `${id}-${key}-err` : `${id}-${key}-hint`}
          className={inputClass}
        />
        {err ? (
          <span id={`${id}-${key}-err`} role="alert" className="mt-1 block text-[12px] text-danger">{err}</span>
        ) : (
          <span id={`${id}-${key}-hint`} className="mt-1 block text-[12px] text-muted">{hint}</span>
        )}
      </div>
    );
  };

  return (
    <section aria-labelledby={`${id}-h`} className="rounded-xl border border-gray-200 bg-white p-4 shadow-sm" data-testid="nudge-settings">
      <h2 id={`${id}-h`} className="text-[15px] font-semibold text-gray-900">Nudges</h2>
      <p className="mt-1 text-[13px] text-muted">
        When nobody on the team has touched an open candidate for a while, the recipients covering that role get a text asking what happened. One digit back moves the card.
      </p>
      <label className="mt-3 flex min-h-10 items-start gap-3 text-[13px]">
        <input
          type="checkbox"
          role="switch"
          aria-checked={value.enabled}
          checked={value.enabled}
          onChange={(e) => onChange({ ...value, enabled: e.target.checked })}
          className="mt-1 h-4 w-4 accent-navy"
        />
        <span>
          <span className="font-medium text-gray-800">Send nudges automatically</span>
          <span className="mt-0.5 block text-muted">Only during contact hours. “Nudge now” on a candidate still works when this is off.</span>
        </span>
      </label>
      <div className="mt-3 grid gap-3 sm:grid-cols-2">
        {hours('after_hours', 'Ask after (hours)', `Since the last team action on the candidate. ${NUDGE_LIMITS.after_hours.min}–${NUDGE_LIMITS.after_hours.max}.`)}
        {hours('cooldown_hours', 'Cooldown (hours)', `Before the same candidate is asked about again. ${NUDGE_LIMITS.cooldown_hours.min}–${NUDGE_LIMITS.cooldown_hours.max}.`)}
      </div>
      <figure className="mt-4 text-[13px]" aria-labelledby={`${id}-preview`}>
        <figcaption id={`${id}-preview`} className="font-medium text-gray-800">What your managers receive</figcaption>
        <div className="mt-1.5 max-w-[420px] space-y-2">
          <div className="rounded-2xl rounded-bl-md bg-page px-3.5 py-2.5 leading-5 text-gray-900">
            <p>{NUDGE_QUESTION}</p>
            <ol className="mt-1.5 space-y-0.5">
              {NUDGE_REPLIES.map((r) => (
                <li key={r.digit} className="flex gap-2">
                  <span className="w-3 shrink-0 font-semibold tabular-nums">{r.digit}</span>
                  <span>{r.label}</span>
                </li>
              ))}
            </ol>
          </div>
          <p className="text-[12px] text-muted">
            Replying 1, 2 or 3 moves the card, then a second text asks: “{NUDGE_NOTES_QUESTION}” — anything other than NO is saved as a note on the candidate. Replying 4 leaves the card where it is and asks again after the cooldown.
          </p>
        </div>
      </figure>
    </section>
  );
}
